import React, { useEffect, useState } from 'react';
import { config } from '@/app/config';
import { get } from '@/utils/fetch';
import { Helmet } from 'react-helmet-async';
import tailwindConfig from '../../tailwind.config.js';
import PlaygroundEditor from './playgroundEditor.jsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { generateGuid } from '@/utils';
import { useSnapshot } from 'valtio'
import { editorState } from './editor-state';
import { editor } from 'monaco-editor';

const Playground = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const tw_config = {
    theme: tailwindConfig.theme
  };

  const isUrlAllowed = (url) => {
    const allowlist = import.meta.env.VITE_LOAD_ALLOWLIST?.split(',') || [];
    return allowlist.some(allowed => url.startsWith(allowed.trim()));
  };

  const handleError = (message) => {
    setErrorMessage(message);
    setIsErrorOpen(true);
  };

  const fetchArtifact = async (id) => {
    var url = "/api/businessobjects/uiartifact/" + id;
    var response = await get(url);
    console.log(response);
    if(response.ErrorCode === 0) {
      var artifact = response.Data?.properties?.artifact;
      editorState.component.jsx = artifact?.jsx || '';
      editorState.component.specification = artifact?.specification || '';      
    }
  }

  useEffect(() => {
    console.log('Playground mounted');

    // Check if connected via id parameter
    const params = new URLSearchParams(window.location.search);
    const hasId = params.get('id') !== null;
    let id = params.get('id');

    if (hasId) {
      if (!id || id.trim() === '') {
        // Id parameter exists but is empty, generate a new GUID
        id = generateGuid();
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('id', id);
        window.history.replaceState({}, '', newUrl.toString());
      } else {
        fetchArtifact(id);
      }

      editorState.component.id = id;
    }

    

    // Check localStorage first
    try {
      const saved = localStorage.getItem('artifact_wip');
      if (saved) {
        const parsedData = JSON.parse(saved);
        const timeDiff = (new Date().getTime() - new Date(parsedData.timestamp).getTime()) / 1000;

        if (timeDiff <= 9) {
          console.log("Found fresh saved content in localStorage");
          setCode(parsedData.content);
          setLoading(false);
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to load from localStorage:', e);
    }

    // Check URL for artifact to load
    const artifactUrl = params.get('load');

    if (artifactUrl) {
      if (isUrlAllowed(artifactUrl)) {
        fetch(artifactUrl)
          .then(response => response.text())
          .then(data => {
            setCode(data);
            setLoading(false);
          })
          .catch(error => {
            console.error('Error fetching code:', error);
            setLoading(false);
          });
      } else {
        handleError(`URL not in allowlist: ${artifactUrl}`);
        setCode('');
        setLoading(false);
      }
    } else {
      setCode('');
      setLoading(false);
    }

    return () => console.log('Playground unmounting');
  }, []);

  return (
    <>
      <Helmet>
        <title>React Artifacts Coding Agent</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <script>{`
    const checkVars = setInterval(() => {
      const root = getComputedStyle(document.documentElement);
      if (root.getPropertyValue('--background')) {
        clearInterval(checkVars);
        if (typeof tailwind != 'undefined') tailwind.config = ${JSON.stringify(tw_config, null, 2)};
      }
    }, 100);
  `}</script>
      </Helmet>

      <div className="h-screen">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xl">Loading...</p>
          </div>
        ) : (
          <PlaygroundEditor
            className="h-full"
            key="stable-key"
            initialText={code}
          />
        )}
      </div>

      <AlertDialog open={isErrorOpen} onOpenChange={setIsErrorOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Error</AlertDialogTitle>
            <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setIsErrorOpen(false)}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Playground;
